import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

const launchDate = new Date("2026-06-01T00:00:00-07:00");

const getTimeRemaining = () => {
  const now = new Date();
  const difference = launchDate.getTime() - now.getTime();

  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isLaunched: true,
    };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
    isLaunched: false,
  };
};

const projects = [
  {
    name: "Honest Dads",
    type: "Featured Product",
    stack: "React Native · FastAPI · PostgreSQL · Product Design",
    summary:
      "A full-stack community platform designed to give dads a more honest, practical, and supportive place to share real parenting experiences.",
    impact:
      "Built as a production-minded product with authentication, community features, future moderation workflows, and scalable backend patterns in mind.",
    details: [
      "Designed the product around trust, anonymity options, and clear community interaction instead of building a generic forum.",
      "Planned a FastAPI and PostgreSQL backend with structured data models for users, posts, comments, badges, moderation, and affiliate-style product recommendations.",
      "Focused on scalable API design, clean separation between frontend and backend concerns, and maintainable product architecture as the application grows.",
      "Used the project to practice full product ownership, including UI decisions, backend modeling, user experience, launch planning, and long-term feature expansion.",
    ],
    highlights: [
      "Community platform",
      "Authentication-ready",
      "Moderation workflows",
      "Product strategy",
    ],
    status:
      "Actively evolving into a deeper case study with backend architecture, screenshots, and product roadmap.",
    images: [
      "/projects/honest-dads/screen-1.png",
      "/projects/honest-dads/screen-2.png",
      "/projects/honest-dads/screen-3.png",
      "/projects/honest-dads/screen-4.png",
      "/projects/honest-dads/screen-5.png",
      "/projects/honest-dads/screen-6.png",
    ],
  },
];

const ProjectsPage = () => {
  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setTimeRemaining(getTimeRemaining());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <main className="px-6 py-28 text-slate-200 md:px-12 lg:px-24">
      <Helmet>
        <title>Projects | Gabriel E.</title>
      </Helmet>

      <section className="mx-auto max-w-6xl">
        <div className="mb-14 max-w-3xl">
          <p className="mb-4 text-sm font-medium uppercase tracking-[0.35em] text-cyan-400">
            Featured Project
          </p>

          <h1 className="text-4xl font-bold tracking-tight text-slate-100 md:text-6xl">
            Honest Dads
          </h1>

          <p className="mt-6 text-lg leading-8 text-slate-400">
            A full-stack product focused on community, parenting support,
            product thinking, backend architecture, and building something real
            from the ground up.
          </p>
        </div>

        <div className="space-y-8">
          {projects.map((project, projectIndex) => (
            <section
              key={project.name}
              className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-2xl shadow-slate-950/40 transition hover:border-cyan-400/40 hover:bg-slate-900/90 md:p-8"
            >
              <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                <div className="max-w-3xl">
                  <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-cyan-400">
                    0{projectIndex + 1}. {project.type}
                  </p>

                  <h2 className="text-2xl font-bold tracking-tight text-slate-100 group-hover:text-cyan-300 md:text-3xl">
                    {project.name}
                  </h2>

                  <p className="mt-3 text-sm font-medium text-slate-500">
                    {project.stack}
                  </p>

                  <p className="mt-5 text-base leading-7 text-slate-400">
                    {project.summary}
                  </p>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 md:w-72">
                  <p className="text-sm font-semibold text-slate-300">
                    Engineering Focus
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    {project.impact}
                  </p>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-cyan-400/30 bg-cyan-400/5 px-5 py-5 text-center">
                <p className="text-sm font-medium uppercase tracking-[0.25em] text-cyan-300">
                  Honest Dads Launch
                </p>

                {timeRemaining.isLaunched ? (
                  <p className="mt-3 text-2xl font-semibold text-slate-100">
                    Live as of June 1, 2026
                  </p>
                ) : (
                  <>
                    <p className="mt-3 text-3xl font-bold tracking-tight text-slate-100">
                      {timeRemaining.days}d {timeRemaining.hours}h{" "}
                      {timeRemaining.minutes}m {timeRemaining.seconds}s
                    </p>

                    <p className="mt-2 text-sm text-slate-500">
                      Counting down to June 1, 2026
                    </p>
                  </>
                )}
              </div>

              <div className="mt-8 grid gap-8 lg:grid-cols-[1.3fr_0.7fr]">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-400">
                    Case Study Notes
                  </h3>

                  <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-400">
                    {project.details.map((detail) => (
                      <li key={detail} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-cyan-400" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-400">
                    Highlights
                  </h3>

                  <div className="mt-5 flex flex-wrap gap-3">
                    {project.highlights.map((highlight) => (
                      <span
                        key={highlight}
                        className="rounded-full border border-slate-700 bg-slate-950/50 px-4 py-2 text-sm text-slate-400 transition hover:border-cyan-400/50 hover:text-cyan-300"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {project.images ? (
                <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {project.images.map((image, index) => (
                    <div
                      key={image}
                      className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/60"
                    >
                      <img
                        src={image}
                        alt={`${project.name} screenshot ${index + 1}`}
                        className="h-full w-full object-cover opacity-90 transition duration-300 hover:scale-[1.02] hover:opacity-100"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              ) : null}

              <p className="mt-6 border-t border-slate-800 pt-5 text-sm text-slate-500">
                {project.status}
              </p>
            </section>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProjectsPage;
